import React, { useEffect, useState } from "react";
import { api } from "../../utils/api";
import { FaPlus, FaTrash, FaEdit, FaStar, FaSpinner, FaToggleOn, FaToggleOff } from "react-icons/fa";

const EMPTY = { name: "", visaType: "", category: "Work Visa", flag: "🌍", country: "", countryName: "", imageUrl: "", text: "", date: "", rating: 5, isActive: true, order: 0 };

const BACKEND_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

function Modal({ title, data, onSave, onClose }) {
    const [form, setForm] = useState(data);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const fileRef = React.useRef();

    const f = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    // Preview source — full URL takes priority, else try randomuser.me
    const previewSrc = form.imageUrl
        ? form.imageUrl.startsWith("http")
            ? form.imageUrl
            : `https://randomuser.me/api/portraits/${form.imageUrl}.jpg`
        : null;

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadError("");
        setUploading(true);
        try {
            const token = localStorage.getItem("lumis_admin_token");
            const fd = new FormData();
            fd.append("image", file);
            const res = await fetch(`${BACKEND_URL}/api/admin/upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.message || "Upload failed");
            setForm((prev) => ({ ...prev, imageUrl: json.url }));
        } catch (err) {
            setUploadError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!form.name || !form.visaType || !form.text) return alert("Name, Visa Type and Text are required.");
        setSaving(true);
        try { await onSave(form); onClose(); }
        catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-lg font-bold text-slate-800">{title}</h2>
                </div>
                <div className="p-6 space-y-4">

                    {/* ── IMAGE UPLOAD ── */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Client Photo</label>
                        <div className="flex items-center gap-4">
                            {/* Avatar preview */}
                            <div className="w-16 h-16 rounded-full border-2 border-dashed border-slate-300 overflow-hidden flex items-center justify-center bg-slate-50 flex-shrink-0">
                                {uploading ? (
                                    <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                ) : previewSrc ? (
                                    <img src={previewSrc} alt="preview" className="w-full h-full object-cover"
                                        onError={(e) => { e.target.style.display = "none"; }} />
                                ) : (
                                    <span className="text-2xl">👤</span>
                                )}
                            </div>

                            <div className="flex-1">
                                <button type="button" onClick={() => fileRef.current.click()}
                                    className="w-full border-2 border-dashed border-blue-300 rounded-xl py-2.5 px-4 text-sm text-blue-600 hover:bg-blue-50 transition font-medium">
                                    {uploading ? "Uploading…" : "📁 Upload Photo"}
                                </button>
                                <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP · max 5 MB</p>
                                {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}
                            </div>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                        </div>

                        {/* Fallback: paste a URL or randomuser.me path */}
                        <div className="mt-2">
                            <input
                                type="text" name="imageUrl" value={form.imageUrl} onChange={f}
                                placeholder="Or paste a URL / randomuser.me path (e.g. men/32)"
                                className="w-full border border-slate-200 rounded-xl px-4 py-2 text-xs text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* ── TEXT FIELDS ── */}
                    {[
                        ["text", "name", "Client Name *", "e.g. Ahmed R."],
                        ["text", "visaType", "Visa / Service Type *", "e.g. Canada Work Visa"],
                        ["text", "category", "Category", "Work Visa | Student Visa | PR Visa | Tourist Visa | Business Visa"],
                        ["text", "flag", "Flag Emoji", "🇨🇦"],
                        ["text", "country", "Country ISO Code", "e.g. ca, gb, nl, au"],
                        ["text", "countryName", "Country Name", "e.g. Canada"],
                        ["text", "date", "Date", "e.g. March 2025"],
                    ].map(([type, name, label, placeholder]) => (
                        <div key={name}>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
                            <input type={type} name={name} value={form[name] || ""} onChange={f} placeholder={placeholder}
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                    ))}

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Testimonial Text *</label>
                        <textarea name="text" value={form.text} onChange={f} rows={4} placeholder="What the client said..."
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Rating (1–5)</label>
                            <input type="number" name="rating" value={form.rating} onChange={f} min={1} max={5}
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Display Order</label>
                            <input type="number" name="order" value={form.order} onChange={f}
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                    </div>
                </div>
                <div className="p-6 border-t border-slate-100 flex gap-3">
                    <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-50 transition">Cancel</button>
                    <button onClick={handleSave} disabled={saving || uploading}
                        className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-60">
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}


export default function AdminTestimonials() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // { mode: "add"|"edit", data }

    const load = async () => {
        setLoading(true);
        const res = await api.getTestimonials();
        setItems(res.data);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const handleSave = async (form) => {
        if (modal.mode === "add") {
            const res = await api.createTestimonial(form);
            setItems((prev) => [res.data, ...prev]);
        } else {
            const res = await api.updateTestimonial(modal.data._id, form);
            setItems((prev) => prev.map((t) => t._id === res.data._id ? res.data : t));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this testimonial?")) return;
        await api.deleteTestimonial(id);
        setItems((prev) => prev.filter((t) => t._id !== id));
    };

    const handleToggle = async (id) => {
        const res = await api.toggleTestimonial(id);
        setItems((prev) => prev.map((t) => t._id === id ? res.data : t));
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Testimonials</h1>
                    <p className="text-slate-500 text-sm mt-1">{items.length} total · {items.filter(t => t.isActive).length} active</p>
                </div>
                <button onClick={() => setModal({ mode: "add", data: EMPTY })}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition shadow-sm">
                    <FaPlus /> Add Testimonial
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-40"><FaSpinner className="animate-spin text-blue-600 text-2xl" /></div>
            ) : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {items.map((t) => (
                        <div key={t._id} className={`bg-white rounded-2xl border p-5 shadow-sm transition ${t.isActive ? "border-slate-100" : "border-slate-200 opacity-60"}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <img
                                        src={t.imageUrl ? `https://randomuser.me/api/portraits/${t.imageUrl}.jpg` : "https://via.placeholder.com/40"}
                                        alt={t.name}
                                        className="w-10 h-10 rounded-full object-cover"
                                        onError={(e) => { e.target.src = "https://ui-avatars.com/api/?name=" + t.name; }}
                                    />
                                    <div>
                                        <p className="font-semibold text-slate-800 text-sm">{t.name}</p>
                                        <p className="text-slate-400 text-xs">{t.flag} {t.visaType}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1 text-yellow-400">
                                    {[...Array(t.rating)].map((_, i) => <FaStar key={i} className="text-xs" />)}
                                </div>
                            </div>
                            <p className="text-slate-600 text-sm italic leading-relaxed line-clamp-3">"{t.text}"</p>
                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-50">
                                <button onClick={() => handleToggle(t._id)} className={`text-sm flex items-center gap-1.5 transition ${t.isActive ? "text-green-600 hover:text-green-700" : "text-slate-400 hover:text-slate-600"}`}>
                                    {t.isActive ? <FaToggleOn className="text-lg" /> : <FaToggleOff className="text-lg" />}
                                    <span className="text-xs">{t.isActive ? "Active" : "Hidden"}</span>
                                </button>
                                <div className="flex gap-2">
                                    <button onClick={() => setModal({ mode: "edit", data: t })}
                                        className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition"><FaEdit /></button>
                                    <button onClick={() => handleDelete(t._id)}
                                        className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition"><FaTrash /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {modal && (
                <Modal
                    title={modal.mode === "add" ? "Add Testimonial" : "Edit Testimonial"}
                    data={modal.data}
                    onSave={handleSave}
                    onClose={() => setModal(null)}
                />
            )}
        </div>
    );
}
